import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { map, Observable, startWith } from 'rxjs';
import { take } from 'rxjs/operators';
import { ChangePersonalInfoRequest } from '../../../../models/users/requests/submissionRequests/change-personal-info-request';
import { LoadingOptions } from '../../../../models/shared/loading-options';
import { MemberUser } from '../../../../models/users/dto/member-user';
import { MediaUtils } from '../../../../utils/media-utils';
import { PersonalInfoSubmissionFormViewModel } from './personal-info-submission-form-view-model';
import { sinValidator } from '../../../../validators/valid-sin.validator';
import { maxFileSizeValidator } from '../../../../ui/form-components/file-uploader/file-upload-field/max-file-size-validator';

type PersonalInfoForm = FormGroup<{
  birthdate: FormControl<string | null>;
  socialInsuranceNumber: FormControl<string>;
  genderId: FormControl<number | null>;
  pronounId: FormControl<number | null>;
  files: FormControl<File[]>;
}>;

@Component({
  selector: 'app-personal-info-submission-form',
  templateUrl: './personal-info-submission-form.component.html',
  styleUrls: ['./personal-info-submission-form.component.scss'],
  providers: [PersonalInfoSubmissionFormViewModel],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PersonalInfoSubmissionFormComponent implements OnChanges {
  @Input() user!: MemberUser;
  @Input() decryptedMemberSIN: string = '';
  @Input() canViewFullSIN: boolean = true;
  @Input() acceptedFileTypes: string[] = ['image/png', 'image/jpeg', 'application/pdf'];
  @Input() allowMultipleFiles: boolean = true;
  @Input() maxFileSizeInMb: number = 3;

  @Output() loadingOpts = new EventEmitter<LoadingOptions>();

  private viewModel = inject(PersonalInfoSubmissionFormViewModel);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  public formGroup: PersonalInfoForm = this.buildForm();
  public formSubmitted = false;

  public readonly genderDropdowns$ = this.viewModel.genderDropdowns$;
  public readonly pronounDropdowns$ = this.viewModel.pronounDropdowns$;

  public readonly customErrorMessages = {
    files: {
      required: $localize`A file upload is required`,
      maxFileSize: $localize`File size exceeds the maximum limit of ${this.maxFileSizeInMb}MB`
    }
  };

  public readonly infoChanged$: Observable<boolean> = this.formGroup.valueChanges.pipe(
    startWith(this.formGroup.value),
    map(() => this.hasInfoChanged())
  );

  constructor() {
    this.viewModel.loadingOpts$.pipe(takeUntilDestroyed()).subscribe(opts => this.loadingOpts.emit(opts));

    this.infoChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(changed => {
      const filesControl = this.formGroup.controls.files;
      if (changed) {
        filesControl.setValidators([Validators.required, maxFileSizeValidator(this.maxFileSizeInMb)]);
        filesControl.enable({ emitEvent: false });
      } else {
        filesControl.clearValidators();
        filesControl.setValue([], { emitEvent: false });
        filesControl.disable({ emitEvent: false });
      }
      filesControl.updateValueAndValidity({ emitEvent: false });
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.user || changes.decryptedMemberSIN || changes.canViewFullSIN) {
      this.resetFormFromUser();
      this.cdr.markForCheck();
    }
  }

  public submitForm(): void {
    this.formSubmitted = true;
    this.formGroup.markAllAsTouched();
    this.cdr.markForCheck();
    if (this.formGroup.invalid) return;

    const request = this.buildRequest();
    this.infoChanged$.pipe(take(1)).subscribe(changed => {
      const files = this.formGroup.controls.files.value;
      if (changed && files.length) {
        Promise.all(files.map(file => MediaUtils.fileToUserFile(file))).then(userFiles => {
          this.viewModel.submitRequest(request, userFiles);
        });
      } else {
        this.viewModel.submitRequest(request);
      }
    });
  }

  private buildForm(): PersonalInfoForm {
    return new FormGroup({
      birthdate: new FormControl<string | null>(null, { validators: [Validators.required] }),
      socialInsuranceNumber: new FormControl<string>('', { nonNullable: true, validators: [sinValidator] }),
      genderId: new FormControl<number | null>(null, { validators: [Validators.required] }),
      pronounId: new FormControl<number | null>(null),
      files: new FormControl<File[]>({ value: [], disabled: true }, { nonNullable: true })
    });
  }

  private resetFormFromUser(): void {
    if (!this.user) return;

    const sinControl = this.formGroup.controls.socialInsuranceNumber;
    const sinValidators = this.canViewFullSIN ? [Validators.required, sinValidator] : [];
    sinControl.setValidators(sinValidators);
    if (this.canViewFullSIN) {
      sinControl.enable({ emitEvent: false });
    } else {
      sinControl.disable({ emitEvent: false });
    }

    this.formGroup.reset(
      {
        birthdate: this.user.birthDate ?? null,
        socialInsuranceNumber: this.decryptedMemberSIN ?? '',
        genderId: this.user.genderId ?? null,
        pronounId: this.user.pronounId ?? null,
        files: []
      },
      { emitEvent: false }
    );
    this.formGroup.controls.files.disable({ emitEvent: false });
    this.formSubmitted = false;
    this.formGroup.updateValueAndValidity();
  }

  private hasInfoChanged(): boolean {
    if (!this.user) return false;
    const value = this.formGroup.getRawValue();
    if (value.genderId !== (this.user.genderId ?? null)) return true;
    if (!this.sameDate(value.birthdate, this.user.birthDate)) return true;
    if (this.canViewFullSIN && value.socialInsuranceNumber !== (this.decryptedMemberSIN ?? '')) return true;
    return false;
  }

  private sameDate(a: string | null, b: string | null | undefined): boolean {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return a.split('T')[0] === b.split('T')[0];
  }

  private buildRequest(): ChangePersonalInfoRequest {
    const value = this.formGroup.getRawValue();
    const request = new ChangePersonalInfoRequest(this.user, this.decryptedMemberSIN);
    request.birthdate = value.birthdate ?? '';
    request.genderId = value.genderId ?? 0;
    request.pronounId = value.pronounId;
    if (this.canViewFullSIN) {
      request.socialInsuranceNumber = value.socialInsuranceNumber;
    }
    return request;
  }
}
